import { FC, useCallback, useEffect, useState } from "react";
import { Box, Card, Typography } from "@mui/material";
import {
  MiniMap,
  Node,
  ReactFlow,
  useNodesInitialized,
  useReactFlow,
} from "@xyflow/react";
import __ from "src/utils/Translations";
import { useParams } from "react-router-dom";
import Controls from "./CustomReactFlow/Controls/Controls";
import useOrgchartStore from "./Utils/store";
import { OrgchartType } from "./FilterBar.models";
import "./CustomReactFlow/MiniMap/_minimap.scss";
import {
  getAutoLayoutWorker,
  getFindNodeWorker,
} from "./workers/getOrgchartWebWorker";
import {
  nodeTypes,
  reactFlowSettings,
  setCenterOptions,
} from "./OrgchartSettings";

const AutoLayoutWorker: Worker | undefined = getAutoLayoutWorker();
const FindNodeWorker: Worker | undefined = getFindNodeWorker();

interface IOrgchartTree {
  orgchartType: OrgchartType;
}

const OrgChartTree: FC<IOrgchartTree> = ({ orgchartType }) => {
  const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
  const { nodeId: urlNodeId } = useParams() as { nodeId: string | undefined };
  const nodesInitialized = useNodesInitialized();
  const flow = useReactFlow();
  const {
    nodes,
    edges,
    isLoading,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    allNodes,
    searchedNodeId,
    setSearchedNodeId,
    wasSearchedNodeCleared,
  } = useOrgchartStore();

  const handleChangeFullScreenMode = () => {
    setFullScreenMode(!fullScreenMode);
  };

  // Initial layout function, calculates positions for nodes
  const initialLayout = useCallback(() => {
    if (isLoading || !searchedNodeId) {
      return null;
    }

    AutoLayoutWorker.postMessage({ nodes, edges });
    AutoLayoutWorker.onmessage = (event) => {
      setNodes(event.data.nodes);
      setEdges(event.data.edges);
      return null;
    };

    return null;
  }, [setNodes, setEdges, edges, nodes, searchedNodeId, isLoading]);

  useEffect(() => {
    const isAutoLayoutWorkerDefined = typeof AutoLayoutWorker;
    if (
      !isLoading &&
      allNodes.length &&
      !searchedNodeId &&
      isAutoLayoutWorkerDefined !== "undefined"
    ) {
      initialLayout();
    } else if (typeof AutoLayoutWorker === "undefined") {
      // eslint-disable-next-line
      console.error("AutoLayout failed -> AutoLayoutWorker is undefined");
    }
    // eslint-disable-next-line
  }, [isLoading, allNodes, searchedNodeId]);

  useEffect(() => {
    if (
      useOrgchartStore.getState().nodeToFocusOn &&
      typeof useOrgchartStore.getState().nodeToFocusOn !== "undefined" &&
      searchedNodeId === null
    ) {
      const position = flow.getNode(
        `${useOrgchartStore.getState()?.nodeToFocusOn?.id}`
      )?.position;
      const node = flow.getNode(
        `${useOrgchartStore.getState()?.nodeToFocusOn?.id}`
      ) as Node;

      const height = node?.measured?.height;
      const width = node?.measured?.width;

      if (
        useOrgchartStore.getState().nodeToFocusOn &&
        useOrgchartStore.getState().nodeToFocusOn?.isCollapsed &&
        position &&
        width &&
        height
      ) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + height / 2,
            setCenterOptions
          );
        });
      } else if (
        useOrgchartStore.getState().nodeToFocusOn &&
        !useOrgchartStore.getState().nodeToFocusOn?.isCollapsed &&
        position &&
        width &&
        height
      ) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            position.x + width / 2,
            position.y + 50 + height * 2,
            setCenterOptions
          );
        });
      }
    }
    // eslint-disable-next-line
  }, [nodes]);

  useEffect(() => {
    if (searchedNodeId !== null && !isLoading) {
      FindNodeWorker.postMessage({ allNodes, searchedNodeId });
      FindNodeWorker.onmessage = (fEvent) => {
        AutoLayoutWorker.postMessage({ nodes: fEvent.data.nodes, edges });
        AutoLayoutWorker.onmessage = (aEvent) => {
          setNodes(aEvent.data.nodes);
          setEdges(aEvent.data.edges);
        };
        useOrgchartStore
          .getState()
          .childrenCollapsedStatus.forEach((_value, key) => {
            useOrgchartStore
              .getState()
              .setChildrenCollapsedStatus(
                new Map(
                  useOrgchartStore.getState().childrenCollapsedStatus
                ).set(
                  key,
                  !fEvent.data.searchRoute.find(
                    (routeId: string) => routeId === key
                  )
                )
              );
          });
      };
    }
    // eslint-disable-next-line
  }, [searchedNodeId, isLoading]);

  useEffect(() => {
    if (searchedNodeId && nodesInitialized) {
      const searchedNode = flow.getNode(searchedNodeId) as Node;
      const height = searchedNode?.measured?.height;
      const width = searchedNode?.measured?.width;
      if (searchedNode && height && width) {
        window.requestAnimationFrame(() => {
          flow.setCenter(
            searchedNode.position.x + width / 2,
            searchedNode.position.y + height / 2,
            setCenterOptions
          );
        });
      }
    }
    // eslint-disable-next-line
  }, [searchedNodeId, nodesInitialized, nodes]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      flow.fitView({ maxZoom: 0.7 });
    });
  }, [flow, fullScreenMode, orgchartType]);

  useEffect(() => {
    if (nodesInitialized) {
      if (urlNodeId && !wasSearchedNodeCleared) {
        setSearchedNodeId(urlNodeId);
      }

      if (AutoLayoutWorker) {
        try {
          AutoLayoutWorker.postMessage({ nodes, edges });

          AutoLayoutWorker.onmessage = (aEvent: MessageEvent) => {
            setNodes(aEvent.data.nodes);
            setEdges(aEvent.data.edges);
          };
        } catch (error) {
          // eslint-disable-next-line
          console.error("Error with AutoLayoutWorker:", error);
        }
      }
    }

    return () => {
      if (AutoLayoutWorker) {
        AutoLayoutWorker.onmessage = null;
      }
    };
    // eslint-disable-next-line
  }, [nodesInitialized]);

  return (
    <Card
      sx={
        fullScreenMode
          ? {
              position: "fixed",
              height: "100vh",
              width: "100vw",
              left: 0,
              top: "60px",
              zIndex: 1000000,
              padding: 0,
            }
          : {
              margin: "16px 0 0 0",
              padding: "0",
              height: "641px",
              width: "100%",
              left: 0,
              top: 0,
            }
      }
    >
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          padding: "8px 16px",
          borderBottom: "1px solid #A1DEFA",
        }}
      >
        <Typography fontWeight="fontWeightMedium">{__(`Struktura`)}</Typography>
      </Box>
      <Box
        sx={{
          height: fullScreenMode ? "calc(100vh - 101px)" : "600px",
          position: "relative",
          contentVisibility: "auto",
        }}
      >
        <ReactFlow
          nodeTypes={nodeTypes}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          {...reactFlowSettings}
        >
          <Controls setFullScreenMode={handleChangeFullScreenMode} />
          {nodes.length <= 60 && (
            <MiniMap
              pannable
              maskColor="#86868640"
              style={{ borderRadius: "5px" }}
            />
          )}
        </ReactFlow>
      </Box>
    </Card>
  );
};

export default OrgChartTree;
